/**
 * In-Memory LRU Cache for Streameater
 * Fast, session-based caching with automatic eviction
 */

import { MEMORY_CACHE_CONFIG } from "./cacheConfig";

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
  size: number; // Approximate size in bytes
}

class MemoryCache {
  private cache: Map<string, CacheEntry<unknown>> = new Map();
  private accessOrder: string[] = [];
  private totalSize: number = 0;
  private maxSize: number = MEMORY_CACHE_CONFIG.MAX_SIZE_MB * 1024 * 1024;

  /**
   * Get item from cache
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key) as CacheEntry<T> | undefined;

    if (!entry) {
      return null;
    }

    // Check if expired
    if (this.isExpired(entry)) {
      this.delete(key);
      return null;
    }

    // Update access order (LRU)
    this.updateAccessOrder(key);

    return entry.data;
  }

  /**
   * Get item even if stale (for stale-while-revalidate)
   */
  getStale<T>(key: string): { data: T; isStale: boolean } | null {
    const entry = this.cache.get(key) as CacheEntry<T> | undefined;

    if (!entry) {
      return null;
    }

    this.updateAccessOrder(key);

    return {
      data: entry.data,
      isStale: this.isExpired(entry),
    };
  }

  /**
   * Set item in cache
   */
  set<T>(key: string, data: T, ttl: number): void {
    const size = this.estimateSize(data);

    // Evict if necessary
    while (
      this.cache.size >= MEMORY_CACHE_CONFIG.MAX_ITEMS ||
      this.totalSize + size > this.maxSize
    ) {
      this.evictOldest();
    }

    // Remove existing entry if present
    if (this.cache.has(key)) {
      this.delete(key);
    }

    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl,
      size,
    };

    this.cache.set(key, entry);
    this.accessOrder.push(key);
    this.totalSize += size;
  }

  /**
   * Delete item from cache
   */
  delete(key: string): boolean {
    const entry = this.cache.get(key);
    if (entry) {
      this.totalSize -= entry.size;
      this.accessOrder = this.accessOrder.filter((k) => k !== key);
      return this.cache.delete(key);
    }
    return false;
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    this.cache.clear();
    this.accessOrder = [];
    this.totalSize = 0;
  }

  /**
   * Clear entries matching a pattern
   */
  clearPattern(pattern: string | RegExp): number {
    let cleared = 0;
    const regex = typeof pattern === "string" ? new RegExp(pattern) : pattern;

    for (const key of this.cache.keys()) {
      if (regex.test(key)) {
        this.delete(key);
        cleared++;
      }
    }

    return cleared;
  }

  /**
   * Get cache statistics
   */
  getStats() {
    let expiredCount = 0;
    let validCount = 0;

    for (const [, entry] of this.cache) {
      if (this.isExpired(entry)) {
        expiredCount++;
      } else {
        validCount++;
      }
    }

    return {
      totalItems: this.cache.size,
      validItems: validCount,
      expiredItems: expiredCount,
      totalSize: this.totalSize,
      maxSize: this.maxSize,
      utilizationPercent: Math.round((this.totalSize / this.maxSize) * 100),
    };
  }

  /**
   * Check if all critical data is cached
   */
  hasCriticalData(keys: string[]): boolean {
    return keys.every((key) => {
      const entry = this.cache.get(key);
      return entry && !this.isExpired(entry);
    });
  }

  private isExpired(entry: CacheEntry<unknown>): boolean {
    return Date.now() - entry.timestamp > entry.ttl;
  }

  private updateAccessOrder(key: string): void {
    const index = this.accessOrder.indexOf(key);
    if (index > -1) {
      this.accessOrder.splice(index, 1);
      this.accessOrder.push(key);
    }
  }

  private evictOldest(): void {
    if (this.accessOrder.length === 0) return;

    const oldestKey = this.accessOrder[0];
    this.delete(oldestKey);
  }

  private estimateSize(data: unknown): number {
    try {
      const str = JSON.stringify(data);
      // Approximate: 2 bytes per character in UTF-16
      return str.length * 2;
    } catch {
      return 1024; // Default 1KB if can't serialize
    }
  }
}

// Singleton instance
export const memoryCache = new MemoryCache();

// Convenience exports
export const getFromMemory = <T>(key: string) => memoryCache.get<T>(key);
export const setInMemory = <T>(key: string, data: T, ttl: number) =>
  memoryCache.set(key, data, ttl);
export const getStaleFromMemory = <T>(key: string) =>
  memoryCache.getStale<T>(key);
export const clearMemoryCache = () => memoryCache.clear();
export const getMemoryCacheStats = () => memoryCache.getStats();
